// src/pages/Matchingedit.jsx
import React, { useState, useRef, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import '../styles/Matching.css';
import { FaBell, FaUserCircle } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

const LOCAL_SELECTED_KEY = 'selectedQuestionsByChapter';
const apiBase = "https://digital.tifleducation.com/admin/public/api";

const Matchingedit = () => {
  const navigate = useNavigate();
  const { paperId } = useParams(); // optional, for new or edit
  const location = useLocation();

  const [activeItem, setActiveItem] = useState('papers');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [user, setUser] = useState({ id: null, name: '', email: '', password: '' });
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState({});
  const [matchingLimit, setMatchingLimit] = useState(0);

  // -------------------- HELPER --------------------
  const parseColumn = (htmlString) => {
    if (!htmlString) return [];
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    return Array.from(tempDiv.querySelectorAll('p')).map(p => p.textContent.trim());
  };

  // -------------------- FETCH DATA --------------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return toast.error('User not logged in.');

        // User info
        const dashRes = await axios.get(`${apiBase}/getDashboard`, { headers: { Authorization: `Bearer ${token}` } });
        if (dashRes.data?.user) setUser({ ...dashRes.data.user, password: '' });

        // Selected board/grade/subject
        const boardId = localStorage.getItem("selectedBoardId");
        const gradeId = localStorage.getItem("selectedGradeId");
        const subjectId = localStorage.getItem("selectedSubjectId");
        if (!boardId || !gradeId || !subjectId) return toast.error("Missing board/grade/subject.");

        // Limit from form
        const regularForm = JSON.parse(localStorage.getItem('regularPaperForm') || '{}');
        setMatchingLimit(Number(regularForm.matching || 0));

        // Fetch questions
        let data = {};
        if (paperId && paperId !== 'new') {
          // Edit existing
          const res = await axios.get(`${apiBase}/papers/${paperId}`, { headers: { Authorization: `Bearer ${token}` } });
          data = res.data?.questions || {};
        } else {
          // Fetch all
          const res = await axios.post(`${apiBase}/papers/fetch-questions`,
            { board: Number(boardId), grade: Number(gradeId), subject: Number(subjectId) },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          data = res.data || {};
        }

        const matchingQs = [];
        Object.entries(data).forEach(([chapterId, chapterData]) => {
          const types = chapterData.types || {};
          Object.entries(types).forEach(([typeLabel, qArray]) => {
            if (String(typeLabel).toLowerCase().includes("matching")) {
              (Array.isArray(qArray) ? qArray : []).forEach(q => {
                matchingQs.push({
                  ...q,
                  chapter_id: q.chapter_id ?? Number(chapterId),
                  optionsLeft: parseColumn(q.left_column),
                  optionsRight: parseColumn(q.right_column)
                });
              });
            }
          });
        });
        setQuestions(matchingQs);

        // Restore selections
        const saved = JSON.parse(localStorage.getItem(LOCAL_SELECTED_KEY) || '{}');
        const selectedMap = {};
        Object.values(saved).forEach(ch => {
          const arr = ch.matching_type || [];
          arr.forEach(id => selectedMap[Number(id)] = true);
        });
        setSelectedQuestions(selectedMap);

      } catch (error) {
        console.error("Error fetching Matching questions:", error.response?.data || error);
        toast.error("Failed to fetch questions.");
      }
    };

    fetchData();
  }, [paperId]);

  // -------------------- SELECT QUESTIONS --------------------
  const handleSelectQuestion = async (qId, chapterId) => {
    const alreadySelected = !!selectedQuestions[qId];
    const currentCount = Object.keys(selectedQuestions).length;

    if (!matchingLimit) return toast.error("No Matching types limit set.");
    if (!alreadySelected && currentCount >= matchingLimit) return toast.error(`You can only select up to ${matchingLimit} Matching questions.`);

    const nextSelected = { ...selectedQuestions };
    if (nextSelected[qId]) delete nextSelected[qId];
    else nextSelected[qId] = true;
    setSelectedQuestions(nextSelected);

    try {
      const saved = JSON.parse(localStorage.getItem(LOCAL_SELECTED_KEY) || "{}");
      const chapterKey = String(chapterId);
      if (!saved[chapterKey]) saved[chapterKey] = {};
      if (!Array.isArray(saved[chapterKey].matching_type)) saved[chapterKey].matching_type = [];

      const arr = saved[chapterKey].matching_type;
      const idx = arr.findIndex(x => Number(x) === Number(qId));
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(Number(qId));
      saved[chapterKey].matching_type = arr;

      localStorage.setItem(LOCAL_SELECTED_KEY, JSON.stringify(saved));

      // API sync only for existing paper
      if (paperId && paperId !== 'new') {
        const token = localStorage.getItem('token');
        await axios.put(`${apiBase}/papers/${paperId}`, { selectedQuestions: saved }, { headers: { Authorization: `Bearer ${token}` } });
      }

      toast.success("Selection saved");
    } catch (error) {
      console.error("Error saving Matching questions:", error.response?.data || error);
      toast.error("Failed to save selection");
    }
  };

  // -------------------- PROFILE SAVE --------------------
  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${apiBase}/update-profile`,
        { name: user.name, password: user.password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Profile updated successfully!");
      setIsModalOpen(false);
      setUser({ ...user, password: '' });
    } catch (error) {
      console.error("Error updating profile:", error.response?.data || error);
      toast.error("Failed to update profile");
    }
  };

  // -------------------- RENDER --------------------
  return (
    <div className="dashboard-container">
      <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} activeItem={activeItem} setActiveItem={setActiveItem} />
      <main className="main-content">
        <div className="matching-header">
          <div className="matching-welcome-text">
            <a onClick={() => navigate(-1)} className="back-link"><i className="fa fa-arrow-left" /></a>
            <span>Matching Type</span>
          </div>
          <div className="header-icons" ref={dropdownRef}>
            <FaBell className="icon" />
            <FaUserCircle className="icon" onClick={() => setIsModalOpen(true)} />
          </div>
        </div>

        <div className="matching-questions-container">
          {questions.length ? questions.map((q, index) => (
            <div key={q.id} className="matching-question-box">
              <div className="matching-question">
                <input
                  type="checkbox"
                  className="matching-checkbox"
                  checked={!!selectedQuestions[q.id]}
                  onChange={() => handleSelectQuestion(q.id, q.chapter_id)}
                  disabled={!selectedQuestions[q.id] && Object.keys(selectedQuestions).length >= matchingLimit}
                />
                <span className="question-text">{`${index + 1}. ${String(q.question_title || q.question || "").replace(/<\/?[^>]+(>|$)/g, "")}`}</span>
              </div>
              <div className="matching-optionss">
                <div className="matching-options">{q.optionsLeft.map((opt, i) => <div key={i}>{opt}</div>)}</div>
                <div className="matching-options">{q.optionsRight.map((opt, i) => <div key={i}>{opt}</div>)}</div>
              </div>
            </div>
          )) : (
            <div className="no-questions-container">
              <div className="no-questions-icon">📄</div>
              <h3>No Matching Questions Found</h3>
              <p style={{ color: '#666' }}>
                {matchingLimit ? "There are currently no matching-type questions for this selection." : "No Matching types limit set. Please set question counts first."}
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Profile Edit Modal */}
      {isModalOpen && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <button style={modalStyles.closeBtn} onClick={() => setIsModalOpen(false)}>&times;</button>
            <div style={modalStyles.userIcon}><FaUserCircle /></div>

            <div style={modalStyles.formGroup}>
              <label>Username</label>
              <input type="text" value={user.name} onChange={e => setUser({ ...user, name: e.target.value })} style={modalStyles.input} />
            </div>
            <div style={modalStyles.formGroup}>
              <label>Email</label>
              <input type="email" value={user.email} disabled style={{ ...modalStyles.input, background: '#f0f0f0', color: '#888' }} />
            </div>
            <div style={modalStyles.formGroup}>
              <label>Password</label>
              <input type="password" value={user.password} onChange={e => setUser({ ...user, password: e.target.value })} style={modalStyles.input} />
            </div>
            <div style={{ textAlign: 'right' }}>
              <button style={modalStyles.saveBtn} onClick={handleSaveProfile}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const modalStyles = {
  overlay: { position: "fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.6)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000 },
  modal: { background:"#fff", padding:"2rem", borderRadius:"12px", width:"90%", maxWidth:"400px", position:"relative", boxShadow:"0 4px 15px rgba(0,0,0,0.2)" },
  closeBtn: { position:"absolute", top:"10px", right:"15px", background:"none", border:"none", fontSize:"1.5rem", cursor:"pointer" },
  userIcon: { textAlign:"center", fontSize:"4rem", color:"#129255", marginBottom:"1rem" },
  formGroup: { marginBottom:"1rem" },
  input: { width:"100%", padding:"0.5rem 0.8rem", borderRadius:"6px", border:"1px solid #ccc", outline:"none" },
  saveBtn: { background:"#129255", color:"#fff", border:"none", padding:"0.6rem 1.2rem", borderRadius:"6px", cursor:"pointer", fontWeight:"bold" }
};

export default Matchingedit;
